import type { SessionRepairStatus } from '../../research/packages/session-repair/types';
import { SESSION_REPAIR_STATUS_LABELS } from '../../research/packages/session-repair/constants';

export type GroupSessionRow = {
  sessionId: number;
  patientCode?: string | null;
  sessionDate?: string | Date | null;
  deviceName?: string | null;
  deviceSensorName?: string | null;
  hasHrData?: boolean;
  repairStatus?: SessionRepairStatus;
  hrEventCount: number;
  status?: 'loading' | 'ready' | 'error';
  error?: string;
  duplicateCount?: number;
  gapCount?: number;
  meanRrMs?: number | null;
  rmssdMs?: number | null;
  lfHfRatio?: number | null;
};

export type GroupSessionsSort = {
  columnId: 'sessionId' | 'patientCode' | 'repairStatus' | 'hrEventCount';
  desc: boolean;
};

const REPAIR_STATUS_SORT_ORDER: Record<SessionRepairStatus, number> = {
  no_hr_data: 0,
  analysis_failed: 1,
  not_analyzed: 2,
  analyzing: 3,
  needs_repair: 4,
  perfect: 5,
  repaired_perfect: 6,
};

export const comparePatientCodes = (
  left: string | null | undefined,
  right: string | null | undefined
) => {
  const leftMissing = !left?.trim();
  const rightMissing = !right?.trim();

  if (leftMissing && rightMissing) {
    return 0;
  }

  if (leftMissing) {
    return 1;
  }

  if (rightMissing) {
    return -1;
  }

  return (left as string).localeCompare(right as string, 'it', {
    sensitivity: 'base',
  });
};

export const compareRepairStatus = (
  left: SessionRepairStatus | undefined,
  right: SessionRepairStatus | undefined
) => {
  const leftOrder = left ? REPAIR_STATUS_SORT_ORDER[left] : -1;
  const rightOrder = right ? REPAIR_STATUS_SORT_ORDER[right] : -1;

  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  const leftLabel = left ? SESSION_REPAIR_STATUS_LABELS[left] : '';
  const rightLabel = right ? SESSION_REPAIR_STATUS_LABELS[right] : '';

  return leftLabel.localeCompare(rightLabel, 'it', { sensitivity: 'base' });
};

export const compareHrEventCount = (left: number, right: number) =>
  left - right;

export const compareSessionDates = (
  left?: string | Date | null,
  right?: string | Date | null
) => {
  const leftTime = left ? new Date(left).getTime() : Number.NaN;
  const rightTime = right ? new Date(right).getTime() : Number.NaN;
  const leftMissing = !left || Number.isNaN(leftTime);
  const rightMissing = !right || Number.isNaN(rightTime);

  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  return leftTime - rightTime;
};

export const compareNullableMetrics = (
  left: number | null | undefined,
  right: number | null | undefined
) => {
  const leftMissing = left == null || !Number.isFinite(left);
  const rightMissing = right == null || !Number.isFinite(right);

  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  return left - right;
};

export const sortGroupSessions = (
  rows: GroupSessionRow[],
  sort: GroupSessionsSort
): GroupSessionRow[] => {
  const direction = sort.desc ? -1 : 1;

  return [...rows].sort((left, right) => {
    let result = 0;

    switch (sort.columnId) {
      case 'patientCode':
        result = comparePatientCodes(left.patientCode, right.patientCode);
        break;
      case 'repairStatus':
        result = compareRepairStatus(left.repairStatus, right.repairStatus);
        break;
      case 'hrEventCount':
        result = compareHrEventCount(left.hrEventCount, right.hrEventCount);
        break;
      case 'sessionId':
      default:
        result = compareHrEventCount(left.sessionId, right.sessionId);
        break;
    }

    if (result !== 0) {
      return result * direction;
    }

    return left.sessionId - right.sessionId;
  });
};

export const DEFAULT_GROUP_SESSIONS_SORT: GroupSessionsSort = {
  columnId: 'sessionId',
  desc: false,
};
